import type {
  BinaryLabel,
  Candle,
  Label,
  ResolvingBarrier,
} from "../types";

/**
 * Triple-barrier labeling: the pure logic that resolves a Predict Round
 * from its Forward path (design spec §6, §14). Shared by the build-time
 * pipeline (to precompute labels per difficulty preset) and runtime (binary
 * derivation, barrier lines). No UI, no chart.
 *
 * From the last visible Context candle (`entry` = its close):
 *  1. recent daily volatility = ATR over the last `atrPeriod` Context candles;
 *  2. barriers = entry ± m·ATR, with a time cap of `nMax` Forward candles;
 *  3. walk Forward one candle at a time using each candle's high/low — the
 *     barrier touched first wins (upper → Up, lower → Down, neither → Sideways).
 *
 * A single Forward candle whose range touches *both* barriers is ambiguous, so
 * the Round is ineligible at that difficulty and labeling returns `null`.
 */

export type Difficulty = {
  /** Barrier width: multiplier on ATR. Larger = wider barriers. */
  m: number;
  /** Time cap: max Forward candles the walk considers before Sideways. */
  nMax: number;
  /** ATR lookback in Context candles (default 14). */
  atrPeriod?: number;
};

export type TripleBarrierResolution = {
  label: Label;
  barrier: ResolvingBarrier;
  /** 1-based index into Forward at which the round resolved. */
  daysToResolve: number;
  upperBarrier: number;
  lowerBarrier: number;
  /** Percent move from entry to the resolving price (e.g. 4.2 = +4.2%). */
  pctMove: number;
};

export type BinaryResolution = {
  label: BinaryLabel;
  barrier: ResolvingBarrier;
  daysToResolve: number;
  upperBarrier: number;
  lowerBarrier: number;
  pctMove: number;
};

const DEFAULT_ATR_PERIOD = 14;

/**
 * True Range of `candle` given the prior candle's close (Wilder). When there is
 * no prior close (first candle of a series) it degrades to the candle's range.
 */
function trueRange(candle: Candle, prevClose: number | undefined): number {
  const highLow = candle.high - candle.low;
  if (prevClose === undefined) return highLow;
  return Math.max(
    highLow,
    Math.abs(candle.high - prevClose),
    Math.abs(candle.low - prevClose),
  );
}

/**
 * Average True Range over the last `period` candles of `context` — a simple
 * arithmetic mean of True Range, per spec §6 ("ATR over the last 14 context
 * candles"), NOT Wilder's RMA-smoothed ATR. See ADR-0003 for why. Each candle's
 * True Range uses the previous Context candle's close, so one extra leading
 * candle is consumed when available. Throws if `context` is empty.
 */
export function atr(context: Candle[], period = DEFAULT_ATR_PERIOD): number {
  if (context.length === 0) {
    throw new Error("atr: context must have at least one candle");
  }
  // The last `period` candles are the window; index `start` is the first of them.
  const window = Math.min(period, context.length);
  const start = context.length - window;

  let sum = 0;
  for (let i = start; i < context.length; i++) {
    const prevClose = i > 0 ? context[i - 1].close : undefined;
    sum += trueRange(context[i], prevClose);
  }
  return sum / window;
}

type WalkResult =
  | { kind: "up" | "down"; index: number; price: number }
  | { kind: "time"; index: number; price: number }
  | { kind: "ineligible" };

/**
 * Walk `forward` against fixed upper/lower barriers, at most `nMax` candles.
 * Returns the first barrier touched, or a time-cap result carrying the close at
 * the cap, or ineligible if a single candle touches both barriers first.
 */
function walk(
  forward: Candle[],
  upperBarrier: number,
  lowerBarrier: number,
  nMax: number,
): WalkResult {
  const steps = Math.min(nMax, forward.length);
  for (let i = 0; i < steps; i++) {
    const candle = forward[i];
    const touchesUpper = candle.high >= upperBarrier;
    const touchesLower = candle.low <= lowerBarrier;
    if (touchesUpper && touchesLower) return { kind: "ineligible" };
    if (touchesUpper) return { kind: "up", index: i + 1, price: upperBarrier };
    if (touchesLower) return { kind: "down", index: i + 1, price: lowerBarrier };
  }
  // Neither barrier touched within the cap: resolve at the last walked close.
  const lastIndex = steps - 1;
  return {
    kind: "time",
    index: steps,
    price: forward[lastIndex].close,
  };
}

function entryFrom(context: Candle[]): number {
  if (context.length === 0) {
    throw new Error("labeling: context must have at least one candle");
  }
  return context[context.length - 1].close;
}

function pctMove(entry: number, resolvingPrice: number): number {
  return ((resolvingPrice - entry) / entry) * 100;
}

/**
 * 3-way triple-barrier resolution. Returns `null` when the round is ineligible
 * at this difficulty (a single Forward candle touches both barriers first) or
 * when there is no Forward path to walk.
 */
export function labelTripleBarrier(
  context: Candle[],
  forward: Candle[],
  difficulty: Difficulty,
): TripleBarrierResolution | null {
  if (forward.length === 0 || difficulty.nMax < 1) return null;

  const entry = entryFrom(context);
  const atrValue = atr(context, difficulty.atrPeriod ?? DEFAULT_ATR_PERIOD);
  const upperBarrier = entry + difficulty.m * atrValue;
  const lowerBarrier = entry - difficulty.m * atrValue;

  const result = walk(forward, upperBarrier, lowerBarrier, difficulty.nMax);
  if (result.kind === "ineligible") return null;

  const label: Label =
    result.kind === "up" ? "up" : result.kind === "down" ? "down" : "sideways";
  const barrier: ResolvingBarrier =
    result.kind === "time" ? "time" : result.kind;

  return {
    label,
    barrier,
    daysToResolve: result.index,
    upperBarrier,
    lowerBarrier,
    pctMove: pctMove(entry, result.price),
  };
}

/**
 * Binary ("quick mode") resolution. The same walk runs, but the time-cap case
 * collapses to the sign of the net move at the cap: `close@cap − entry` > 0 →
 * Up (Higher), < 0 → Down (Lower). Exact-flat time-cap rounds are dropped
 * (`null`), as are ineligible and empty-Forward rounds. Barrier-hit cases match
 * the 3-way direction.
 */
export function labelBinary(
  context: Candle[],
  forward: Candle[],
  difficulty: Difficulty,
): BinaryResolution | null {
  if (forward.length === 0 || difficulty.nMax < 1) return null;

  const entry = entryFrom(context);
  const atrValue = atr(context, difficulty.atrPeriod ?? DEFAULT_ATR_PERIOD);
  const upperBarrier = entry + difficulty.m * atrValue;
  const lowerBarrier = entry - difficulty.m * atrValue;

  const result = walk(forward, upperBarrier, lowerBarrier, difficulty.nMax);
  if (result.kind === "ineligible") return null;

  let label: BinaryLabel;
  if (result.kind === "up" || result.kind === "down") {
    label = result.kind;
  } else {
    // Time cap: collapse to the sign of the net move; drop exact-flat.
    const net = result.price - entry;
    if (net === 0) return null;
    label = net > 0 ? "up" : "down";
  }

  return {
    label,
    barrier: result.kind === "time" ? "time" : result.kind,
    daysToResolve: result.index,
    upperBarrier,
    lowerBarrier,
    pctMove: pctMove(entry, result.price),
  };
}
